import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiAlertTriangle,
  FiBell,
  FiCheckCircle,
  FiFolder,
  FiShield,
  FiUsers,
  FiX,
} from "react-icons/fi";
import { FaRupeeSign } from "react-icons/fa";
import useAuth from "../../hooks/useAuth";
import useSocket from "../../hooks/useSocket";
import { notificationService } from "../../services/notification.service";
import {
  acceptNotificationToast,
  notificationTarget,
  notificationToastCategory,
  relativeNotificationTime,
  resetNotificationToasts,
  stackNotificationToasts,
} from "../../utils/notificationToast";
import { playNotificationAlert, armNotificationSoundUnlock } from "../../utils/notificationSound";
import "../../styles/notificationPanel.css";

const TOAST_MS = 8000;
const EXIT_MS = 180;
const QUEUE_LIMIT = 12;

const ICONS = {
  confirmation: FiCheckCircle,
  seized: FiShield,
  case: FiFolder,
  payment: FaRupeeSign,
  agency: FiUsers,
  warning: FiAlertTriangle,
  general: FiBell,
};

export default function NotificationToast() {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [toasts, setToasts] = useState([]);
  const viewer = auth?.user;
  const timers = useRef(new Map());

  useEffect(() => {
    resetNotificationToasts();
    setToasts([]);
  }, [auth?.token]);

  useEffect(() => {
    armNotificationSoundUnlock();
    const pending = timers.current;
    return () => {
      pending.forEach((timer) => window.clearTimeout(timer));
      pending.clear();
    };
  }, []);

  const removeToast = useCallback((key) => {
    const timer = timers.current.get(key);
    if (timer) window.clearTimeout(timer);
    timers.current.delete(key);
    setToasts((current) => current.map((item) => (item.key === key ? { ...item, leaving: true } : item)));
    window.setTimeout(() => {
      setToasts((current) => current.filter((item) => item.key !== key));
    }, EXIT_MS);
  }, []);

  const accept = useCallback(
    (detail) => {
      if (!viewer) return;
      const decision = acceptNotificationToast(detail, {
        userId: viewer.id || viewer._id || viewer.userId,
        companyId: viewer.companyId,
      });
      if (!decision.show) return;
      playNotificationAlert();
      const key = `${decision.toast.id || decision.toast.confirmationId || decision.toast.title}:${Date.now()}`;
      setToasts((current) => [...current, { ...decision.toast, key }].slice(-QUEUE_LIMIT));
      timers.current.set(key, window.setTimeout(() => removeToast(key), TOAST_MS));
      window.dispatchEvent(new Event("app:notifications-changed"));
    },
    [removeToast, viewer]
  );

  useEffect(() => {
    const onArrived = (event) => accept(event.detail || {});
    window.addEventListener("app:notification-arrived", onArrived);
    return () => window.removeEventListener("app:notification-arrived", onArrived);
  }, [accept]);

  useSocket(
    useCallback((event, payload) => {
      if (event !== "notifications:new" || !payload) return;
      window.dispatchEvent(
        new CustomEvent("app:notification-arrived", {
          detail: { ...payload, source: payload.source || "socket" },
        })
      );
    }, [])
  );

  const openToast = async (toast) => {
    removeToast(toast.key);
    const path = notificationTarget(toast, viewer?.role);
    if (toast.id && auth?.token) {
      try {
        await notificationService.markRead(toast.id, auth.token);
        window.dispatchEvent(new Event("app:notifications-changed"));
      } catch {
        /* The bell reload reports a failed read. */
      }
    }
    if (path) navigate(path);
  };

  const shown = stackNotificationToasts(toasts);

  if (!shown.length) return null;

  return (
    <div className="notification-toasts" aria-live="polite">
      {shown.map((toast) => {
        const category = notificationToastCategory(toast.type);
        const Icon = ICONS[category] || FiBell;
        const path = notificationTarget(toast, viewer?.role);
        const when = relativeNotificationTime(toast.createdAt);
        const agency = toast.agencyName || "";
        return (
          <article
            key={toast.key}
            className={`notification-toast notification-toast--${category}${toast.leaving ? " is-leaving" : ""}`}
            role="status"
          >
            <span className={`notification-toast__icon notification-toast__icon--${category}`} aria-hidden="true">
              <Icon />
            </span>
            <div className="notification-toast__main">
              <div className="notification-toast__head">
                <button
                  type="button"
                  className="notification-toast__title"
                  onClick={() => openToast(toast)}
                  disabled={!path}
                >
                  {toast.title}
                </button>
                <button
                  type="button"
                  className="notification-toast__close"
                  aria-label="Dismiss notification"
                  onClick={() => removeToast(toast.key)}
                >
                  <FiX />
                </button>
              </div>
              {toast.message ? (
                <button
                  type="button"
                  className="notification-toast__message"
                  onClick={() => openToast(toast)}
                  disabled={!path}
                >
                  {toast.message}
                </button>
              ) : null}
              <div className="notification-toast__meta">
                {toast.vehicleNumber ? (
                  <span className="notification-item__vehicle">{toast.vehicleNumber}</span>
                ) : null}
                {agency ? <span className="notification-toast__agency">{agency || toast.agencyName}</span> : null}
                {when ? <time dateTime={toast.createdAt || undefined}>{when}</time> : null}
                <span className="notification-toast__unread">Unread</span>
              </div>
              {path ? (
                <button type="button" className="notification-toast__action" onClick={() => openToast(toast)}>
                  View Details
                </button>
              ) : null}
            </div>
          </article>
        );
      })}
    </div>
  );
}
